import { tierName, type League, type Membership } from '@h2h/shared';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { ArrowRight, Trophy } from '../../components/ui/icons';
import { useJoinLeague, useCurrentRound, useMyEntry, useMyStanding } from '../../hooks/useQueries';
import { FOCUSED_LEAGUE_ID, identityFor } from '../../data/leagues';
import { cap, ordinalSuffix } from '../../lib/format';
import { isRoundInPlay } from './helpers';

export function WorldCupHero({
  league,
  joined,
  uid,
  membership,
  onPlay
}: {
  league: League | undefined;
  joined: boolean;
  uid: string | null;
  membership: Membership | undefined;
  onPlay: () => void;
}) {
  const join = useJoinLeague(uid);
  const roundId = league?.currentRoundId ?? null;
  const { data: round } = useCurrentRound(league?.leagueId, roundId);
  const { data: entry } = useMyEntry(league?.leagueId, roundId, uid);
  const { data: standing } = useMyStanding(league?.leagueId, roundId, uid);

  const name = league?.name ?? identityFor(FOCUSED_LEAGUE_ID)?.name ?? 'Football Tournament';
  // The big background watermark is the last two digits of a year in the league
  // NAME (e.g. "… 2026" → "26"). A generic name with no year (e.g. "Football
  // Tournament") shows no badge — so this is driven entirely by the server-side
  // `league.name`, no hardcoded year in the bundle.
  const yearInName = name.match(/\b(\d{4})\b/);
  const badge = yearInName ? yearInName[1].slice(2) : null;
  const settled = round?.status === 'SETTLED';
  const inPlay = isRoundInPlay(round?.status);
  const open = round?.status === 'OPEN' || round?.status === 'LOCKING';
  const needsPicks = open && !entry;
  const handle = () => {
    if (!joined) {
      if (!join.isPending) join.mutate(FOCUSED_LEAGUE_ID, { onSuccess: onPlay });
      return;
    }
    onPlay();
  };

  const cta = !joined
    ? 'Join'
    : settled
      ? 'See results'
      : needsPicks
        ? 'Build your cards'
        : 'Play now';

  const roundLabel = round?.label ?? 'Next round';
  const rank = standing?.rankInGroup;
  const tier = membership?.tier;

  const topMeta = 'Football';

  return (
    <button
      type="button"
      onClick={handle}
      disabled={join.isPending}
      className="tap relative w-full overflow-hidden rounded-[30px] bg-[radial-gradient(125%_95%_at_82%_-12%,#19311F_0%,#10131A_48%,#07070B_100%)] text-left text-white shadow-[0_30px_56px_-24px_rgba(0,0,0,0.6)] disabled:opacity-80"
    >
      <div className="pointer-events-none absolute -right-[60px] -top-[70px] h-[230px] w-[230px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.30),transparent_70%)]" />
      {badge ? (
        <div className="pointer-events-none absolute right-[14px] top-[-30px] select-none text-[150px] font-extrabold leading-none tracking-[-0.06em] text-white/[0.05] tabular-nums">
          {badge}
        </div>
      ) : null}

      <div className="relative px-[18px] pb-5 pt-[18px]">
        <div className="flex items-center justify-between gap-3">
          {inPlay ? (
            <div className="inline-flex items-center gap-[7px] rounded-full border border-accent/40 bg-accent/[0.16] px-[11px] py-[5px]">
              <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-accent" />
              <span className="text-[10px] font-extrabold tracking-[0.08em] text-accent">LIVE NOW</span>
            </div>
          ) : (
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-[11px] py-[5px]">
              <span className="text-[10px] font-extrabold tracking-[0.08em] text-white/70">
                {settled ? 'ROUND SETTLED' : 'OPEN SOON'}
              </span>
            </div>
          )}
          <span className="text-right text-[11px] font-semibold tracking-wide text-white/55">{topMeta}</span>
        </div>

        <div className="mt-4 flex items-center gap-[13px]">
          <NationalFlagEmblem leagueId={FOCUSED_LEAGUE_ID} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="text-[22px] font-extrabold leading-[1.05] tracking-tight text-white">{name}</div>
            <div className="mt-0.5 text-[12.5px] font-medium text-white/60">
              {roundLabel} · free to play
            </div>
          </div>
        </div>

        {joined && rank != null && tier != null ? (
          <div className="mt-[15px] flex items-center gap-[9px] rounded-[16px] border border-white/[0.09] bg-white/[0.06] px-[13px] py-[11px]">
            <Trophy width={15} height={15} strokeWidth={2} className="text-[#E9B45C]" />
            <span className="text-[12.5px] font-semibold text-white/75">
              You&apos;re{' '}
              <span className="font-extrabold text-white">
                {rank}
                {ordinalSuffix(rank)}
              </span>{' '}
              in {cap(tierName(tier))}
            </span>
          </div>
        ) : null}

        <div className="relative mt-[14px] flex items-center justify-center gap-[9px] overflow-hidden rounded-[16px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_18px_32px_-14px_rgba(36,197,106,0.6)]">
          <div className="pointer-events-none absolute inset-y-0 w-[40%] animate-h2h-sheen bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)]" />
          <span className="relative text-[15px] font-extrabold tracking-snug text-white">
            {join.isPending ? 'Joining…' : cta}
          </span>
          <ArrowRight width={16} height={16} strokeWidth={2.6} className="relative text-white" />
        </div>
      </div>
    </button>
  );
}
